import { Shaders } from './shaders/shaders';
import * as THREE from 'three';
import { ClipTask, ElevationGradientRepeat, PointShape, PointSizeType, TreeType } from '../defines';
import { Utils } from '../utils';
import { ClassificationScheme } from './ClassificationScheme';
import { resourcePath } from '../potreeState';
import { Gradients } from './Gradients';
import type { SourceInfoById } from './Sources';

//
// how to calculate the radius of a projected sphere in screen space
// http://stackoverflow.com/questions/21648630/radius-of-projected-sphere-in-screen-space
// http://stackoverflow.com/questions/3717226/radius-of-projected-sphere
//

/** Uniform with optional type hint for legacy Potree shaders */
interface PotreeUniform<T> {
  type: string;
  value: T;
}

/** Material parameters for PointCloudMaterial constructor */
interface PointCloudMaterialParameters {
  size?: number;
  minSize?: number;
  maxSize?: number;
  treeType?: number;
}

/** Classification entry */
interface ClassificationEntry {
  color: number[];
  visible: boolean;
}

/** Classification lookup (can be indexed by number or string like 'DEFAULT') */
interface ClassificationLUT {
  [key: string]: ClassificationEntry | undefined;
  [key: number]: ClassificationEntry | undefined;
}

/** Clip box with inverse matrix */
interface ClipBox {
  inverse: THREE.Matrix4;
  box: {
    getActualClipTask(): number;
  };
}

/**
 * Clip sphere — the sphere analog of {@link ClipBox}. `inverse` maps world →
 * unit-sphere-local space (build the source object with uniform scale =
 * radius); PotreeRenderer composes it with each rendering camera per frame,
 * so a camera-following sphere needs no per-frame material writes.
 */
export interface ClipSphere {
  inverse: THREE.Matrix4;
  sphere: {
    getActualClipTask(): number;
  };
}

/** Clip plane with inverse matrix */
interface ClipPlane {
  inverse: THREE.Matrix4;
  plane: {
    actualClipTask: number;
    heatmapIsolate?: boolean;
  };
}

/** Clustered segment */
interface ClusteredSegment {
  clipTask: number;
}

/** Gradient entry: [offset, color] */
type GradientEntry = [number, THREE.Color];

/** Potree shader defines stored as a Map */
type DefinesMap = Map<string, string>;

/** Attribute descriptor */
interface AttributeDescriptor {
  type: string;
  value: unknown[];
}

/** Base type for PointCloudMaterial with Potree-specific overrides */
interface PotreeRawShaderMaterial {
  defines: DefinesMap;
  uniforms: Record<string, PotreeUniform<unknown>>;
  dispatchEvent(event: { type: string; target?: unknown; [key: string]: unknown }): void;
}

// Cast the base class to allow custom defines (Map instead of Record) and custom event types
class RawShaderMaterialBase extends (THREE.RawShaderMaterial as unknown as {
  new (): Omit<THREE.RawShaderMaterial, 'defines' | 'uniforms' | 'dispatchEvent'> & PotreeRawShaderMaterial;
}) {}

/**
 * Default per-point alpha for density (X-ray) mode. With additive alpha
 * blending, roughly 1/alpha overlapping points saturate a pixel to white.
 */
export const DEFAULT_DENSITY_ALPHA = 0.0048;

export class PointCloudMaterial extends RawShaderMaterialBase {
  visibleNodesTexture: THREE.DataTexture;
  gradientTexture: THREE.Texture;
  gradientTextures: Record<string, THREE.Texture>;
  matcapTexture: THREE.Texture;
  classificationTexture: THREE.DataTexture;
  ranges: Map<string, [number, number] | null>;
  attributes: Record<string, AttributeDescriptor>;

  clipBoxes: ClipBox[];
  clipSpheres: ClipSphere[] = [];
  clipPolygons: unknown[];
  clusteredSegments: ClusteredSegment[];
  clipPlanes: ClipPlane[];
  classification: ClassificationLUT;
  /** Source ID visibility/color map, set per-pointcloud each frame */
  sources: SourceInfoById | null;
  /** Whether to use logarithmic depth buffer encoding in shaders */
  useLogarithmicDepthBuffer: boolean;

  private _pointSizeType: number;
  private _shape: number;
  private _densityMode: boolean;
  private _useClipBox: boolean;
  private _weighted: boolean;
  private _gradientName: string;
  private _gradient: GradientEntry[];
  private _matcap: string;
  private _treeType: number;
  private _useEDL: boolean;
  private _activeAttributeName: string | null;
  private _defaultIntensityRangeChanged: boolean;
  private _defaultElevationRangeChanged: boolean;
  private _defaultExtraRangeChanged = false;
  private _initialHeightMin = 0;
  private _initialHeightMax = 0;
  private _initialIntensityMin = 0;
  private _initialIntensityMax = 0;
  private _hiddenListeners: Record<string, unknown[]> | undefined;

  constructor(parameters: PointCloudMaterialParameters = {}) {
    super();

    this.visibleNodesTexture = Utils.generateDataTexture(2048, 1, new THREE.Color(0xffffff));
    this.visibleNodesTexture.minFilter = THREE.NearestFilter;
    this.visibleNodesTexture.magFilter = THREE.NearestFilter;

    const getValid = <T>(a: T | undefined, b: T): T => {
      if (a !== undefined) {
        return a;
      } else {
        return b;
      }
    };

    const pointSize = getValid(parameters.size, 1.0);
    const minSize = getValid(parameters.minSize, 2.0);
    const maxSize = getValid(parameters.maxSize, 50.0);
    const treeType = getValid(parameters.treeType, TreeType.OCTREE);

    this._pointSizeType = PointSizeType.FIXED;
    this._shape = PointShape.SQUARE;
    this._densityMode = false;
    this._useClipBox = false;
    this.clipBoxes = [];
    this.clipPolygons = [];
    this.clusteredSegments = [];
    this.clipPlanes = [];
    this._weighted = false;
    this._gradientName = 'SPECTRAL';
    this._gradient = Gradients.SPECTRAL;
    this.gradientTexture = PointCloudMaterial.generateGradientTexture(this._gradient);

    // Generate all gradient textures for heatmap support
    this.gradientTextures = {
      gradient_spectral: PointCloudMaterial.generateGradientTexture(Gradients.SPECTRAL),
      gradient_plasma: PointCloudMaterial.generateGradientTexture(Gradients.PLASMA),
      gradient_yellow_green: PointCloudMaterial.generateGradientTexture(Gradients.YELLOW_GREEN),
      gradient_viridis: PointCloudMaterial.generateGradientTexture(Gradients.VIRIDIS),
      gradient_inferno: PointCloudMaterial.generateGradientTexture(Gradients.INFERNO),
      gradient_grayscale: PointCloudMaterial.generateGradientTexture(Gradients.GRAYSCALE),
      gradient_turbo: PointCloudMaterial.generateGradientTexture(Gradients.TURBO),
      gradient_rainbow: PointCloudMaterial.generateGradientTexture(Gradients.RAINBOW),
      gradient_contour: PointCloudMaterial.generateGradientTexture(Gradients.CONTOUR),
      gradient_PURE_PURPLE: PointCloudMaterial.generateGradientTexture(Gradients.PURE_PURPLE),
    };
    this._matcap = 'matcap.jpg';
    this.matcapTexture = PointCloudMaterial.generateMatcapTexture(this._matcap);
    this.lights = false;
    this.fog = false;
    this._treeType = treeType;
    this._useEDL = false;
    this.defines = new Map();

    this.ranges = new Map();

    this._activeAttributeName = null;
    this.sources = null;
    this.useLogarithmicDepthBuffer = false;

    this._defaultIntensityRangeChanged = false;
    this._defaultElevationRangeChanged = false;

    {
      const [width, height] = [256, 1];
      const data = new Uint8Array(width * 4);
      const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
      texture.magFilter = THREE.NearestFilter;
      texture.needsUpdate = true;

      this.classificationTexture = texture;
    }

    this.attributes = {
      position: { type: 'fv', value: [] },
      color: { type: 'fv', value: [] },
      normal: { type: 'fv', value: [] },
      intensity: { type: 'f', value: [] },
      classification: { type: 'f', value: [] },
      returnNumber: { type: 'f', value: [] },
      numberOfReturns: { type: 'f', value: [] },
      pointSourceID: { type: 'f', value: [] },
      indices: { type: 'fv', value: [] },
    };

    this.uniforms = {
      level: { type: 'f', value: 0.0 },
      vnStart: { type: 'f', value: 0.0 },
      spacing: { type: 'f', value: 1.0 },
      blendHardness: { type: 'f', value: 2.0 },
      blendDepthSupplement: { type: 'f', value: 0.0 },
      fov: { type: 'f', value: 1.0 },
      screenWidth: { type: 'f', value: 1.0 },
      screenHeight: { type: 'f', value: 1.0 },
      near: { type: 'f', value: 0.1 },
      far: { type: 'f', value: 1.0 },
      uColor: { type: 'c', value: new THREE.Color(0xffffff) },
      uOpacity: { type: 'f', value: 1.0 },
      uDensityAlpha: { type: 'f', value: DEFAULT_DENSITY_ALPHA },
      size: { type: 'f', value: pointSize },
      minSize: { type: 'f', value: minSize },
      maxSize: { type: 'f', value: maxSize },
      octreeSize: { type: 'f', value: 0 },
      bbSize: { type: 'fv', value: [0, 0, 0] },
      elevationRange: { type: '2fv', value: [0, 0] },

      clipBoxCount: { type: 'f', value: 0 },
      clipPolygonCount: { type: 'i', value: 0 },
      clipBoxes: { type: 'Matrix4fv', value: [] },
      clipPolygons: { type: '3fv', value: [] },
      clipPolygonVCount: { type: 'iv', value: [] },
      clipPolygonVP: { type: 'Matrix4fv', value: [] },

      visibleNodes: { type: 't', value: this.visibleNodesTexture },
      pcIndex: { type: 'f', value: 0 },
      gradient: { type: 't', value: this.gradientTexture },
      classificationLUT: { type: 't', value: this.classificationTexture },
      uHQDepthMap: { type: 't', value: null },
      toModel: { type: 'Matrix4f', value: [] },
      diffuse: { type: 'fv', value: [1, 1, 1] },
      transition: { type: 'f', value: 0.5 },

      intensityRange: { type: 'fv', value: [0, 0] },

      intensity_gbc: { type: 'fv', value: [1, 0, 0] },
      uRGB_gbc: { type: 'fv', value: [1, 0, 0] },
      wRGB: { type: 'f', value: 1 },
      wIntensity: { type: 'f', value: 0 },
      wElevation: { type: 'f', value: 0 },
      wClassification: { type: 'f', value: 0 },
      wReturnNumber: { type: 'f', value: 0 },
      wSourceID: { type: 'f', value: 0 },
      useOrthographicCamera: { type: 'b', value: false },
      elevationGradientRepat: { type: 'i', value: ElevationGradientRepeat.CLAMP },
      uShadowColor: { type: '3fv', value: [0, 0, 0] },

      uExtraScale: { type: 'f', value: 1 },
      uExtraOffset: { type: 'f', value: 0 },
      uExtraRange: { type: '2fv', value: [0, 1] },
      uExtraGammaBrightContr: { type: '3fv', value: [1, 0, 0] },

      uFilterReturnNumberRange: { type: 'fv', value: [0, 7] },
      uFilterNumberOfReturnsRange: { type: 'fv', value: [0, 7] },
      uFilterGPSTimeClipRange: { type: 'fv', value: [0, 7] },
      uFilterPointSourceIDClipRange: { type: 'fv', value: [0, 65535] },
      matcapTextureUniform: { type: 't', value: this.matcapTexture },
      backfaceCulling: { type: 'b', value: false },
      clipPlanesCount: { type: 'f', value: 0 },
      clipPlanes: { type: 'Matrix4fv', value: [] },

      // Eye-space (camera-relative) frustum bounds for color_type_world_position
      // encoding. min.xy/max.xy = ortho frustum extents (left/right, bottom/top).
      // min.z/max.z = camera near/far (positive depth from camera). Reader
      // reconstructs world position via the active camera's inverse view matrix.
      uEyeMin: { type: '3fv', value: [-1, -1, 0] },
      uEyeMax: { type: '3fv', value: [1, 1, 1] },

      // WallSketch edge distance uniforms for precise distance-based point coloring
      wallSketchEdgeTexture: { type: 't', value: null },
      wallSketchEdgeCount: { type: 'i', value: 0 },
      wallSketchSDFEnabled: { type: 'b', value: false },
      wallSketchSDFMaxDistance: { type: 'f', value: 5.0 },
      wallSketchSDFGradient: { type: 'i', value: 0 },
      wallSketchSDFFloorHeight: { type: 'f', value: 0 },
      wallSketchSDFHeightTolerance: { type: 'f', value: 2.0 },
      // Clip method: 0=NONE, 2=SHOW_INSIDE (isolate), 3=SHOW_OUTSIDE (hide)
      wallSketchSDFClipMethod: { type: 'i', value: 0 },
      wallSketchSDFClipTolerance: { type: 'f', value: 1.0 },

      // Floor 3D mesh edge distance uniforms — parallel SDF path keyed on a
      // separate `floorMesh3DSDFEnabled` toggle so it can coexist with the
      // skeleton SDF. Same uniform shape; the shader applies them
      // independently (a point hit by both gets the floor pass last).
      floorMesh3DEdgeTexture: { type: 't', value: null },
      floorMesh3DEdgeCount: { type: 'i', value: 0 },
      floorMesh3DSDFEnabled: { type: 'b', value: false },
      floorMesh3DSDFMaxDistance: { type: 'f', value: 5.0 },
      floorMesh3DSDFGradient: { type: 'i', value: 0 },
      floorMesh3DSDFFloorHeight: { type: 'f', value: 0 },
      floorMesh3DSDFHeightTolerance: { type: 'f', value: 2.0 },
      floorMesh3DSDFClipMethod: { type: 'i', value: 0 },
      floorMesh3DSDFClipTolerance: { type: 'f', value: 1.0 },
    };

    this.classification = ClassificationScheme as unknown as ClassificationLUT;

    const defaultAttrs = this.defaultAttributeValues as Record<string, number[]>;
    defaultAttrs.normal = [0, 0, 0];
    defaultAttrs.classification = [0, 0, 0];
    defaultAttrs.indices = [0, 0, 0, 0];

    this.vertexShader = Shaders['pointcloud.vs'];
    this.fragmentShader = Shaders['pointcloud.fs'];

    this.updateShaderSource();
  }

  setDefine(key: string, value: string | undefined | null) {
    if (value !== undefined && value !== null) {
      if (this.defines.get(key) !== value) {
        this.defines.set(key, value);
        this.updateShaderSource();
      }
    } else {
      this.removeDefine(key);
    }
  }

  removeDefine(key: string) {
    this.defines.delete(key);
  }

  updateShaderSource() {
    let vs = Shaders['pointcloud.vs'];
    let fs = Shaders['pointcloud.fs'];
    const definesString = this.getDefines();

    const vsVersionIndex = vs.indexOf('#version ');
    const fsVersionIndex = fs.indexOf('#version ');

    if (vsVersionIndex >= 0) {
      vs = vs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      vs = `${definesString}\n${vs}`;
    }

    if (fsVersionIndex >= 0) {
      fs = fs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      fs = `${definesString}\n${fs}`;
    }

    this.vertexShader = vs;
    this.fragmentShader = fs;

    if (this.opacity === 1.0) {
      this.blending = THREE.NoBlending;
      this.transparent = false;
      this.depthTest = true;
      this.depthWrite = true;
      this.depthFunc = THREE.LessEqualDepth;
    } else if (this.opacity < 1.0 && !this.useEDL) {
      this.blending = THREE.AdditiveBlending;
      this.transparent = true;
      this.depthTest = false;
      this.depthWrite = true;
      this.depthFunc = THREE.AlwaysDepth;
    }

    if (this.weighted) {
      this.blending = THREE.AdditiveBlending;
      this.transparent = true;
      this.depthTest = true;
      this.depthWrite = false;
    }

    this.needsUpdate = true;
  }

  getDefines() {
    const defines: string[] = [];

    if (this.pointSizeType === PointSizeType.FIXED) {
      defines.push('#define fixed_point_size');
    } else if (this.pointSizeType === PointSizeType.ATTENUATED) {
      defines.push('#define attenuated_point_size');
    } else if (this.pointSizeType === PointSizeType.ADAPTIVE) {
      defines.push('#define adaptive_point_size');
    }

    if (this._densityMode) {
      defines.push('#define density_mode');
    }

    if (this.shape === PointShape.SQUARE) {
      defines.push('#define square_point_shape');
    } else if (this.shape === PointShape.CIRCLE) {
      defines.push('#define circle_point_shape');
    } else if (this.shape === PointShape.PARABOLOID) {
      defines.push('#define paraboloid_point_shape');
    }

    if (this._useEDL) {
      defines.push('#define use_edl');
    }

    if (this.activeAttributeName) {
      const attributeName = this.activeAttributeName.replace(/[^a-zA-Z0-9]/g, '_');

      defines.push(`#define color_type_${attributeName}`);
    }

    if (this._treeType === TreeType.OCTREE) {
      defines.push('#define tree_type_octree');
    } else if (this._treeType === TreeType.KDTREE) {
      defines.push('#define tree_type_kdtree');
    }

    if (this.weighted) {
      defines.push('#define weighted_splats');
    }

    if (this.uniforms.wallSketchSDFEnabled && (this.uniforms.wallSketchSDFEnabled as PotreeUniform<boolean>).value) {
      defines.push('#define skeleton_sdf_enabled');
    }

    if (this.uniforms.floorMesh3DSDFEnabled && (this.uniforms.floorMesh3DSDFEnabled as PotreeUniform<boolean>).value) {
      defines.push('#define floor_mesh3d_sdf_enabled');
    }

    for (const [, value] of this.defines) {
      defines.push(value);
    }

    return defines.join('\n');
  }

  setClipBoxes(clipBoxes: ClipBox[]) {
    if (!clipBoxes) {
      return;
    }

    const doUpdate = this.clipBoxes.length !== clipBoxes.length && (clipBoxes.length === 0 || this.clipBoxes.length === 0);

    (this.uniforms.clipBoxCount as PotreeUniform<number>).value = this.clipBoxes.length;
    this.clipBoxes = clipBoxes;

    if (doUpdate) {
      this.updateShaderSource();
    }

    (this.uniforms.clipBoxes as PotreeUniform<Float32Array>).value = new Float32Array(this.clipBoxes.length * 16);

    for (let i = 0; i < this.clipBoxes.length; i++) {
      const box = clipBoxes[i];

      (this.uniforms.clipBoxes as PotreeUniform<Float32Array>).value.set(box.inverse.elements, 16 * i);
    }

    for (let i = 0; i < (this.uniforms.clipBoxes as PotreeUniform<Float32Array>).value.length; i++) {
      if (Number.isNaN((this.uniforms.clipBoxes as PotreeUniform<Float32Array>).value[i])) {
        (this.uniforms.clipBoxes as PotreeUniform<Float32Array>).value[i] = Infinity;
      }
    }
  }

  /**
   * Clip spheres — the sphere analog of {@link setClipBoxes}. Only the COUNT
   * feeds the shader (via the `num_clipspheres` define); the per-sphere
   * view→sphere-local matrices and clip tasks are read off this list and
   * composed against each rendering camera in PotreeRenderer, per frame.
   * Recompiles only when the count crosses 0 ↔ N.
   */
  setClipSpheres(clipSpheres: ClipSphere[]) {
    if (!clipSpheres) {
      return;
    }

    const doUpdate = this.clipSpheres.length !== clipSpheres.length && (clipSpheres.length === 0 || this.clipSpheres.length === 0);

    this.clipSpheres = clipSpheres;

    if (doUpdate) {
      this.updateShaderSource();
    }
  }

  setClipPlanes(clipPlanes: ClipPlane[]) {
    if (!clipPlanes) return;

    const doUpdate = this.clipPlanes.length !== clipPlanes.length && (clipPlanes.length === 0 || this.clipPlanes.length === 0);

    this.clipPlanes = clipPlanes;
    (this.uniforms.clipPlanesCount as PotreeUniform<number>).value = this.clipPlanes.length;

    if (doUpdate) {
      this.updateShaderSource();
    }

    (this.uniforms.clipPlanes as PotreeUniform<Float32Array>).value = new Float32Array(this.clipPlanes.length * 16);

    for (let i = 0; i < this.clipPlanes.length; i++) {
      const plane = clipPlanes[i];

      (this.uniforms.clipPlanes as PotreeUniform<Float32Array>).value.set(plane.inverse.elements, 16 * i);
    }

    for (let i = 0; i < (this.uniforms.clipPlanes as PotreeUniform<Float32Array>).value.length; i++) {
      if (Number.isNaN((this.uniforms.clipPlanes as PotreeUniform<Float32Array>).value[i])) {
        (this.uniforms.clipPlanes as PotreeUniform<Float32Array>).value[i] = Infinity;
      }
    }
  }

  setClusteredSegments(clusteredSegments: ClusteredSegment[]) {
    this.clusteredSegments = clusteredSegments;
  }

  setClipPolygons(clipPolygons: unknown[], _maxPolygonVertices?: number) {
    if (!clipPolygons) {
      return;
    }

    this.clipPolygons = clipPolygons;

    const doUpdate = this.clipPolygons.length !== clipPolygons.length;

    if (doUpdate) {
      this.updateShaderSource();
    }
  }

  get gradient() {
    return this._gradient;
  }

  set gradient(value) {
    if (this._gradient !== value) {
      this._gradient = value;
      const oldTexture = this.gradientTexture;
      this.gradientTexture = PointCloudMaterial.generateGradientTexture(this._gradient);
      (this.uniforms.gradient as PotreeUniform<THREE.Texture>).value = this.gradientTexture;

      // Dispose of the old texture to force renderer cache update
      if (oldTexture && oldTexture.dispose) {
        oldTexture.dispose();
      }

      // Mark texture as needing update
      this.gradientTexture.needsUpdate = true;
    }
  }

  set gradientName(value: string) {
    this._gradientName = value;
  }

  get gradientName() {
    return this._gradientName;
  }

  get matcap() {
    return this._matcap;
  }

  set matcap(value) {
    if (this._matcap !== value) {
      this._matcap = value;
      this.matcapTexture = PointCloudMaterial.generateMatcapTexture(this._matcap);
      (this.uniforms.matcapTextureUniform as PotreeUniform<THREE.Texture>).value = this.matcapTexture;
    }
  }
  get useOrthographicCamera() {
    return (this.uniforms.useOrthographicCamera as PotreeUniform<boolean>).value;
  }

  set useOrthographicCamera(value: boolean) {
    if ((this.uniforms.useOrthographicCamera as PotreeUniform<boolean>).value !== value) {
      (this.uniforms.useOrthographicCamera as PotreeUniform<boolean>).value = value;
    }
  }
  get backfaceCulling() {
    return (this.uniforms.backfaceCulling as PotreeUniform<boolean>).value;
  }

  set backfaceCulling(value: boolean) {
    if ((this.uniforms.backfaceCulling as PotreeUniform<boolean>).value !== value) {
      (this.uniforms.backfaceCulling as PotreeUniform<boolean>).value = value;
      this.dispatchEvent({ type: 'backface_changed', target: this });
    }
  }

  recomputeClassification() {
    const classification = this.classification;
    const data = (this.classificationTexture.image as { data: Uint8Array }).data;

    const width = 256;
    const black = [1, 1, 1, 1];

    let valuesChanged = false;

    for (let i = 0; i < width; i++) {
      let color: number[];
      let visible = true;

      const entry = classification[i] ?? classification[i % 32] ?? classification['DEFAULT'];
      if (entry) {
        color = entry.color;
        visible = entry.visible;
      } else {
        color = black;
      }

      const r = Math.floor(255 * color[0]);
      const g = Math.floor(255 * color[1]);
      const b = Math.floor(255 * color[2]);
      const a = visible ? Math.floor(255 * color[3]) : 0;

      if (data[4 * i + 0] !== r) {
        data[4 * i + 0] = r;
        valuesChanged = true;
      }

      if (data[4 * i + 1] !== g) {
        data[4 * i + 1] = g;
        valuesChanged = true;
      }

      if (data[4 * i + 2] !== b) {
        data[4 * i + 2] = b;
        valuesChanged = true;
      }

      if (data[4 * i + 3] !== a) {
        data[4 * i + 3] = a;
        valuesChanged = true;
      }
    }

    if (valuesChanged) {
      this.classificationTexture.needsUpdate = true;

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get spacing() {
    return (this.uniforms.spacing as PotreeUniform<number>).value;
  }

  set spacing(value: number) {
    if ((this.uniforms.spacing as PotreeUniform<number>).value !== value) {
      (this.uniforms.spacing as PotreeUniform<number>).value = value;
    }
  }

  get useClipBox() {
    return this._useClipBox;
  }

  set useClipBox(value) {
    if (this._useClipBox !== value) {
      this._useClipBox = value;
      this.updateShaderSource();
    }
  }

  get elevationGradientRepat() {
    return (this.uniforms.elevationGradientRepat as PotreeUniform<number>).value;
  }

  set elevationGradientRepat(mode: number) {
    (this.uniforms.elevationGradientRepat as PotreeUniform<number>).value = mode;
  }

  get weighted() {
    return this._weighted;
  }

  set weighted(value) {
    if (this._weighted !== value) {
      this._weighted = value;
      this.updateShaderSource();
    }
  }

  get fov() {
    return (this.uniforms.fov as PotreeUniform<number>).value;
  }

  set fov(value: number) {
    if ((this.uniforms.fov as PotreeUniform<number>).value !== value) {
      (this.uniforms.fov as PotreeUniform<number>).value = value;
    }
  }

  get screenWidth() {
    return (this.uniforms.screenWidth as PotreeUniform<number>).value;
  }

  set screenWidth(value: number) {
    if ((this.uniforms.screenWidth as PotreeUniform<number>).value !== value) {
      (this.uniforms.screenWidth as PotreeUniform<number>).value = value;
    }
  }

  get screenHeight() {
    return (this.uniforms.screenHeight as PotreeUniform<number>).value;
  }

  set screenHeight(value: number) {
    if ((this.uniforms.screenHeight as PotreeUniform<number>).value !== value) {
      (this.uniforms.screenHeight as PotreeUniform<number>).value = value;
    }
  }

  get near() {
    return (this.uniforms.near as PotreeUniform<number>).value;
  }

  set near(value: number) {
    if ((this.uniforms.near as PotreeUniform<number>).value !== value) {
      (this.uniforms.near as PotreeUniform<number>).value = value;
    }
  }

  get far() {
    return (this.uniforms.far as PotreeUniform<number>).value;
  }

  set far(value: number) {
    if ((this.uniforms.far as PotreeUniform<number>).value !== value) {
      (this.uniforms.far as PotreeUniform<number>).value = value;
    }
  }

  get opacity() {
    return (this.uniforms.uOpacity as PotreeUniform<number>).value;
  }

  set opacity(value: number) {
    if (this.uniforms && this.uniforms.uOpacity) {
      if ((this.uniforms.uOpacity as PotreeUniform<number>).value !== value) {
        (this.uniforms.uOpacity as PotreeUniform<number>).value = value;
        this.updateShaderSource();
        this.dispatchEvent({
          type: 'opacity_changed',
          target: this,
        });
        this.dispatchEvent({
          type: 'material_property_changed',
          target: this,
        });
      }
    }
  }

  get activeAttributeName() {
    return this._activeAttributeName;
  }

  set activeAttributeName(value: string | null) {
    if (this._activeAttributeName !== value) {
      this._activeAttributeName = value;

      this.updateShaderSource();
      this.dispatchEvent({
        type: 'active_attribute_changed',
        target: this,
      });

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get pointSizeType() {
    return this._pointSizeType;
  }

  set pointSizeType(value: number) {
    if (this._pointSizeType !== value) {
      this._pointSizeType = value;
      this.updateShaderSource();
      this.dispatchEvent({
        type: 'point_size_type_changed',
        target: this,
      });
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get useEDL() {
    return this._useEDL;
  }

  set useEDL(value) {
    if (this._useEDL !== value) {
      this._useEDL = value;
      this.updateShaderSource();
    }
  }

  get color() {
    return (this.uniforms.uColor as PotreeUniform<THREE.Color>).value;
  }

  set color(value: THREE.Color) {
    if (!(this.uniforms.uColor as PotreeUniform<THREE.Color>).value.equals(value)) {
      (this.uniforms.uColor as PotreeUniform<THREE.Color>).value.copy(value);

      this.dispatchEvent({
        type: 'color_changed',
        target: this,
      });
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get densityMode() {
    return this._densityMode;
  }

  set densityMode(value) {
    if (this._densityMode !== value) {
      this._densityMode = value;
      this.updateShaderSource();
      this.dispatchEvent({ type: 'density_mode_changed', target: this });
    }
  }

  /** Per-point alpha used by density (X-ray) mode; see DEFAULT_DENSITY_ALPHA. */
  get densityAlpha(): number {
    return (this.uniforms.uDensityAlpha as PotreeUniform<number>).value;
  }

  set densityAlpha(value: number) {
    const uniform = this.uniforms.uDensityAlpha as PotreeUniform<number>;
    if (uniform.value !== value) {
      // Plain uniform: no shader rebuild needed, the renderer uploads it per draw.
      uniform.value = value;
      this.dispatchEvent({ type: 'material_property_changed', target: this });
    }
  }

  get shape() {
    return this._shape;
  }

  set shape(value: number) {
    if (this._shape !== value) {
      this._shape = value;
      this.updateShaderSource();
      this.dispatchEvent({ type: 'point_shape_changed', target: this });
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get treeType() {
    return this._treeType;
  }

  set treeType(value: number) {
    if (this._treeType !== value) {
      this._treeType = value;
      this.updateShaderSource();
    }
  }

  get bbSize() {
    return (this.uniforms.bbSize as PotreeUniform<number[]>).value;
  }

  set bbSize(value: number[]) {
    (this.uniforms.bbSize as PotreeUniform<number[]>).value = value;
  }

  get size() {
    return (this.uniforms.size as PotreeUniform<number>).value;
  }

  set size(value: number) {
    if ((this.uniforms.size as PotreeUniform<number>).value !== value) {
      (this.uniforms.size as PotreeUniform<number>).value = value;

      this.dispatchEvent({
        type: 'point_size_changed',
        target: this,
      });
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get minSize() {
    return (this.uniforms.minSize as PotreeUniform<number>).value;
  }

  set minSize(value: number) {
    if ((this.uniforms.minSize as PotreeUniform<number>).value !== value) {
      (this.uniforms.minSize as PotreeUniform<number>).value = value;

      this.dispatchEvent({
        type: 'point_size_changed',
        target: this,
      });
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get elevationRange() {
    return (this.uniforms.elevationRange as PotreeUniform<number[]>).value;
  }

  set elevationRange(value: number[]) {
    const changed = (this.uniforms.elevationRange as PotreeUniform<number[]>).value[0] !== value[0] || (this.uniforms.elevationRange as PotreeUniform<number[]>).value[1] !== value[1];

    if (changed) {
      (this.uniforms.elevationRange as PotreeUniform<number[]>).value = value;

      this._defaultElevationRangeChanged = true;

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get heightMin() {
    return (this.uniforms.elevationRange as PotreeUniform<number[]>).value[0];
  }

  set heightMin(value: number) {
    this.elevationRange = [value, this.elevationRange[1]];
  }

  get initialHeightMin() {
    return this._initialHeightMin;
  }

  set initialHeightMin(value: number) {
    this._initialHeightMin = value;
  }

  get heightMax() {
    return (this.uniforms.elevationRange as PotreeUniform<number[]>).value[1];
  }

  set heightMax(value: number) {
    this.elevationRange = [this.elevationRange[0], value];
  }

  get initialHeightMax() {
    return this._initialHeightMax;
  }

  set initialHeightMax(value: number) {
    this._initialHeightMax = value;
  }

  get transition() {
    return (this.uniforms.transition as PotreeUniform<number>).value;
  }

  set transition(value: number) {
    (this.uniforms.transition as PotreeUniform<number>).value = value;
  }

  get intensityMin() {
    return (this.uniforms.intensityRange as PotreeUniform<number[]>).value[0];
  }

  set intensityMin(value: number) {
    this.intensityRange = [value, this.intensityRange[1]];
  }

  get initialIntensityMin() {
    return this._initialIntensityMin;
  }

  set initialIntensityMin(value: number) {
    this._initialIntensityMin = value;
  }

  get intensityMax() {
    return (this.uniforms.intensityRange as PotreeUniform<number[]>).value[1];
  }

  set intensityMax(value: number) {
    this.intensityRange = [this.intensityRange[0], value];
  }

  get initialIntensityMax() {
    return this._initialIntensityMax;
  }

  set initialIntensityMax(value: number) {
    this._initialIntensityMax = value;
  }

  get intensityRange() {
    return (this.uniforms.intensityRange as PotreeUniform<number[]>).value;
  }

  set intensityRange(value: number[]) {
    if (!(value instanceof Array && value.length === 2)) {
      return;
    }

    if (value[0] === (this.uniforms.intensityRange as PotreeUniform<number[]>).value[0] && value[1] === (this.uniforms.intensityRange as PotreeUniform<number[]>).value[1]) {
      return;
    }

    (this.uniforms.intensityRange as PotreeUniform<number[]>).value = value;

    if (!this._defaultIntensityRangeChanged) {
      this._initialIntensityMin = value[0];
      this._initialIntensityMax = value[1];
    }
    this._defaultIntensityRangeChanged = true;

    this.dispatchEvent({
      type: 'material_property_changed',
      target: this,
    });
  }

  get intensityGamma() {
    return (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[0];
  }

  set intensityGamma(value: number) {
    if ((this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[0] !== value) {
      (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[0] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get intensityContrast() {
    return (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[2];
  }

  set intensityContrast(value: number) {
    if ((this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[2] !== value) {
      (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[2] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get intensityBrightness() {
    return (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[1];
  }

  set intensityBrightness(value: number) {
    if ((this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[1] !== value) {
      (this.uniforms.intensity_gbc as PotreeUniform<number[]>).value[1] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbGamma() {
    return (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[0];
  }

  set rgbGamma(value: number) {
    if ((this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[0] !== value) {
      (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[0] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbContrast() {
    return (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[2];
  }

  set rgbContrast(value: number) {
    if ((this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[2] !== value) {
      (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[2] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbBrightness() {
    return (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[1];
  }

  set rgbBrightness(value: number) {
    if ((this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[1] !== value) {
      (this.uniforms.uRGB_gbc as PotreeUniform<number[]>).value[1] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraGamma() {
    return (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[0];
  }

  set extraGamma(value: number) {
    if ((this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[0] !== value) {
      (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[0] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraBrightness() {
    return (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[1];
  }

  set extraBrightness(value: number) {
    if ((this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[1] !== value) {
      (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[1] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraContrast() {
    return (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[2];
  }

  set extraContrast(value: number) {
    if ((this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[2] !== value) {
      (this.uniforms.uExtraGammaBrightContr as PotreeUniform<number[]>).value[2] = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  getRange(attributeName: string) {
    return this.ranges.get(attributeName);
  }

  setRange(attributeName: string, newRange: [number, number] | null) {
    let rangeChanged = false;

    const oldRange = this.ranges.get(attributeName);

    if (oldRange != null && newRange != null) {
      rangeChanged = oldRange[0] !== newRange[0] || oldRange[1] !== newRange[1];
    } else {
      rangeChanged = true;
    }

    this.ranges.set(attributeName, newRange);

    if (rangeChanged) {
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraRange() {
    return (this.uniforms.uExtraRange as PotreeUniform<number[]>).value;
  }

  set extraRange(value: number[]) {
    if (!(value instanceof Array && value.length === 2)) {
      return;
    }

    if (value[0] === (this.uniforms.uExtraRange as PotreeUniform<number[]>).value[0] && value[1] === (this.uniforms.uExtraRange as PotreeUniform<number[]>).value[1]) {
      return;
    }

    (this.uniforms.uExtraRange as PotreeUniform<number[]>).value = value;

    this._defaultExtraRangeChanged = true;

    this.dispatchEvent({
      type: 'material_property_changed',
      target: this,
    });
  }

  get weightRGB() {
    return (this.uniforms.wRGB as PotreeUniform<number>).value;
  }

  set weightRGB(value: number) {
    if ((this.uniforms.wRGB as PotreeUniform<number>).value !== value) {
      (this.uniforms.wRGB as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightIntensity() {
    return (this.uniforms.wIntensity as PotreeUniform<number>).value;
  }

  set weightIntensity(value: number) {
    if ((this.uniforms.wIntensity as PotreeUniform<number>).value !== value) {
      (this.uniforms.wIntensity as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightElevation() {
    return (this.uniforms.wElevation as PotreeUniform<number>).value;
  }

  set weightElevation(value: number) {
    if ((this.uniforms.wElevation as PotreeUniform<number>).value !== value) {
      (this.uniforms.wElevation as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightClassification() {
    return (this.uniforms.wClassification as PotreeUniform<number>).value;
  }

  set weightClassification(value: number) {
    if ((this.uniforms.wClassification as PotreeUniform<number>).value !== value) {
      (this.uniforms.wClassification as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightReturnNumber() {
    return (this.uniforms.wReturnNumber as PotreeUniform<number>).value;
  }

  set weightReturnNumber(value: number) {
    if ((this.uniforms.wReturnNumber as PotreeUniform<number>).value !== value) {
      (this.uniforms.wReturnNumber as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightSourceID() {
    return (this.uniforms.wSourceID as PotreeUniform<number>).value;
  }

  set weightSourceID(value: number) {
    if ((this.uniforms.wSourceID as PotreeUniform<number>).value !== value) {
      (this.uniforms.wSourceID as PotreeUniform<number>).value = value;
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  static generateGradientTexture(gradient: GradientEntry[]) {
    const size = 64;

    // create canvas
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    // get context
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get 2d context');

    // draw gradient
    context.rect(0, 0, size, size);
    const ctxGradient = context.createLinearGradient(0, 0, size, size);

    for (let i = 0; i < gradient.length; i++) {
      const step = gradient[i];

      ctxGradient.addColorStop(step[0], '#' + step[1].getHexString());
    }

    context.fillStyle = ctxGradient;
    context.fill();

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    texture.minFilter = THREE.LinearFilter;
    // @ts-expect-error - Potree uses non-standard wrap/repeat properties
    texture.wrap = THREE.RepeatWrapping;
    // @ts-expect-error - Potree uses non-standard wrap/repeat properties
    texture.repeat = 2;

    return texture;
  }

  static generateMatcapTexture(matcap: string) {
    const url = resourcePath + '/textures/matcap/' + matcap;
    const texture = new THREE.TextureLoader().load(url);
    texture.magFilter = texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    return texture;
  }

  disableEvents() {
    if (this._hiddenListeners === undefined) {
      // @ts-expect-error - Accessing internal THREE.js EventDispatcher _listeners
      this._hiddenListeners = this._listeners;
      // @ts-expect-error - Accessing internal THREE.js EventDispatcher _listeners
      this._listeners = {};
    }
  }

  enableEvents() {
    // @ts-expect-error - Accessing internal THREE.js EventDispatcher _listeners
    this._listeners = this._hiddenListeners;
    this._hiddenListeners = undefined;
  }

  isolatedClipBoxes() {
    return this.clipBoxes?.filter((box) => box.box.getActualClipTask() !== ClipTask.HIGHLIGHT && box.box.getActualClipTask() !== ClipTask.GRAYSCALE) || [];
  }

  isolatedClipSpheres() {
    return this.clipSpheres?.filter((sphere) => sphere.sphere.getActualClipTask() !== ClipTask.HIGHLIGHT && sphere.sphere.getActualClipTask() !== ClipTask.GRAYSCALE) || [];
  }

  isolatedClipSegments() {
    return this.clusteredSegments?.filter((cluster) => cluster.clipTask !== ClipTask.HIGHLIGHT && cluster.clipTask !== ClipTask.GRAYSCALE && cluster.clipTask !== ClipTask.NONE) || [];
  }

  isolatedClipPlanes() {
    return (
      this.clipPlanes?.filter((plane) => plane.plane.actualClipTask !== ClipTask.HIGHLIGHT && plane.plane.actualClipTask !== ClipTask.GRAYSCALE && !(plane.plane.actualClipTask === ClipTask.HEATMAP && !plane.plane.heatmapIsolate)) || []
    );
  }

  // WallSketch SDF getters/setters
  get wallSketchSDFEnabled() {
    return (this.uniforms.wallSketchSDFEnabled as PotreeUniform<boolean>).value;
  }

  set wallSketchSDFEnabled(value: boolean) {
    if ((this.uniforms.wallSketchSDFEnabled as PotreeUniform<boolean>).value !== value) {
      (this.uniforms.wallSketchSDFEnabled as PotreeUniform<boolean>).value = value;
      this.updateShaderSource();
    }
  }

  setWallSketchEdges(edgeTexture: THREE.Texture | null, edgeCount: number, maxDistance: number, gradient: number, floorHeight: number, heightTolerance: number, clipMethod: number, clipTolerance: number) {
    (this.uniforms.wallSketchEdgeTexture as PotreeUniform<THREE.Texture | null>).value = edgeTexture;
    (this.uniforms.wallSketchEdgeCount as PotreeUniform<number>).value = edgeCount;
    (this.uniforms.wallSketchSDFMaxDistance as PotreeUniform<number>).value = maxDistance;
    (this.uniforms.wallSketchSDFGradient as PotreeUniform<number>).value = gradient;
    (this.uniforms.wallSketchSDFFloorHeight as PotreeUniform<number>).value = floorHeight;
    (this.uniforms.wallSketchSDFHeightTolerance as PotreeUniform<number>).value = heightTolerance;
    (this.uniforms.wallSketchSDFClipMethod as PotreeUniform<number>).value = clipMethod;
    (this.uniforms.wallSketchSDFClipTolerance as PotreeUniform<number>).value = clipTolerance;
  }

  clearWallSketchSDF() {
    (this.uniforms.wallSketchEdgeTexture as PotreeUniform<THREE.Texture | null>).value = null;
    (this.uniforms.wallSketchEdgeCount as PotreeUniform<number>).value = 0;
    this.wallSketchSDFEnabled = false;
  }

  get floorMesh3DSDFEnabled() {
    return (this.uniforms.floorMesh3DSDFEnabled as PotreeUniform<boolean>).value;
  }

  set floorMesh3DSDFEnabled(value: boolean) {
    if ((this.uniforms.floorMesh3DSDFEnabled as PotreeUniform<boolean>).value !== value) {
      (this.uniforms.floorMesh3DSDFEnabled as PotreeUniform<boolean>).value = value;
      this.updateShaderSource();
    }
  }

  setFloorMesh3DEdges(edgeTexture: THREE.Texture | null, edgeCount: number, maxDistance: number, gradient: number, floorHeight: number, heightTolerance: number, clipMethod: number, clipTolerance: number) {
    (this.uniforms.floorMesh3DEdgeTexture as PotreeUniform<THREE.Texture | null>).value = edgeTexture;
    (this.uniforms.floorMesh3DEdgeCount as PotreeUniform<number>).value = edgeCount;
    (this.uniforms.floorMesh3DSDFMaxDistance as PotreeUniform<number>).value = maxDistance;
    (this.uniforms.floorMesh3DSDFGradient as PotreeUniform<number>).value = gradient;
    (this.uniforms.floorMesh3DSDFFloorHeight as PotreeUniform<number>).value = floorHeight;
    (this.uniforms.floorMesh3DSDFHeightTolerance as PotreeUniform<number>).value = heightTolerance;
    (this.uniforms.floorMesh3DSDFClipMethod as PotreeUniform<number>).value = clipMethod;
    (this.uniforms.floorMesh3DSDFClipTolerance as PotreeUniform<number>).value = clipTolerance;
    // Note: the uniforms are uploaded to the GL program inside
    // `PotreeRenderer.ts` (Potree uses its own custom GL pipeline, not
    // three.js' standard material upload), so simply setting `value` here
    // is enough. The renderer reads from `material.uniforms.*.value`
    // every frame.
  }

  clearFloorMesh3DSDF() {
    (this.uniforms.floorMesh3DEdgeTexture as PotreeUniform<THREE.Texture | null>).value = null;
    (this.uniforms.floorMesh3DEdgeCount as PotreeUniform<number>).value = 0;
    this.floorMesh3DSDFEnabled = false;
  }
}
